import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import Header from "components/header/Header";
import Greeting from "containers/greeting/Greeting";
import Skills from "containers/skills/Skills";
import Footer from "components/footer/Footer";
import BackendShowcase from "components/backendShowcase/BackendShowcase";
import BackendStats from "components/backendStats/BackendStats";
import LiveCode from "../../components/liveCode/LiveCode";
import { useScrollAnimation } from "hooks/useGSAP";
import type { ThemeWithOptionalSetterProps } from "types";

interface HomeSectionProps {
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
}

const HomeSection = React.forwardRef<HTMLDivElement, HomeSectionProps>(
  ({ eyebrow, title, description, children, className = "" }, ref) => (
    <section
      className={`home-section-band border-t border-border ${className}`}
    >
      <div className="relative z-10 max-w-6xl px-4 py-16 mx-auto sm:px-6 md:py-20 lg:px-8 lg:py-24">
        <div className="mb-9 grid gap-4 md:grid-cols-[0.8fr_1.2fr] md:items-end">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-accent">
              {eyebrow}
            </p>
            <h2 className="mt-3 text-2xl font-bold tracking-normal font-display text-text-primary sm:text-3xl">
              {title}
            </h2>
          </div>
          <p className="max-w-2xl text-sm leading-relaxed text-text-secondary md:justify-self-end md:text-right">
            {description}
          </p>
        </div>
        <div ref={ref}>{children}</div>
      </div>
    </section>
  ),
);

HomeSection.displayName = "HomeSection";

const Home: React.FC<ThemeWithOptionalSetterProps> = (props) => {
  const backendStatsRef = useRef<HTMLDivElement>(null);
  const liveCodeRef = useRef<HTMLDivElement>(null);
  const backendShowcaseRef = useRef<HTMLDivElement>(null);
  const skillsRef = useRef<HTMLDivElement>(null);

  const { animate } = useScrollAnimation<HTMLDivElement>();

  useEffect(() => {
    animate(() => {
      const sections = [
        backendStatsRef,
        liveCodeRef,
        backendShowcaseRef,
        skillsRef,
      ];

      sections.forEach((ref) => {
        if (!ref.current) return;
        gsap.fromTo(
          ref.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: ref.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          },
        );
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="home-aesthetic-shell bg-bg-primary text-text-primary">
      <Header theme={props.theme} setTheme={props.setTheme} />
      <Greeting theme={props.theme} />

      <main className="relative z-10">
        <HomeSection
          ref={backendStatsRef}
          eyebrow="Platform Snapshot"
          title="A quick read on the engineering signal."
          description="Backend depth, API delivery, and product-minded execution grouped into a cleaner, more scannable first section."
        >
          <BackendStats />
        </HomeSection>

        <HomeSection
          ref={liveCodeRef}
          eyebrow="Live Architecture"
          title="A code-first view of how I build."
          description="A quieter architecture view that pairs profile and career layers, keeping the portfolio focused, readable, and responsive across devices."
          className="bg-bg-elevated/20"
        >
          <LiveCode />
        </HomeSection>

        <HomeSection
          ref={backendShowcaseRef}
          eyebrow="Systems Toolkit"
          title="Architecture, APIs, data, and delivery."
          description="Tabbed technical depth gets more breathing room inside a full-width band while keeping the interaction familiar."
        >
          <BackendShowcase />
        </HomeSection>

        <HomeSection
          ref={skillsRef}
          eyebrow="Core Strengths"
          title="The tools behind the outcomes."
          description="Skills stay visual and animated, but the surrounding layout now feels like a deliberate chapter instead of another block on the page."
          className="bg-bg-elevated/20"
        >
          <Skills theme={props.theme} />
        </HomeSection>
      </main>
      <Footer theme={props.theme} />
    </div>
  );
};

export default Home;
